import React, { useCallback, useEffect, useState } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import { Loader2, X } from 'src/components/common/icons';
import { ITableColumn, ITableConfig, Table } from 'src/components/common/table';
import PagedResultSet from 'src/models/common/PagedResultSet';
import { CheckboxField, Field } from './Master';
import { ILeaseRecordsApiError, LeaseRecordsErrorAlert, parseLeaseRecordsApiError } from '../LeaseRecordsApiError';

interface IBNoticeRow {
    id: string;
    mm1099No: number;
    year: number;
    noticeSeqNo: number;
    noticeName: string;
    tinFromDb: string;
    irsNtype: string;
    irsNdate?: string;
    tsdiDb?: string;
    buWh?: string;
    noTinName?: string;
    dupIrsNotice?: string;
    g1IrsNotice3Yr2Bs?: string;
    bn1SDate?: string;
    bn2SDate?: string;
    bn1RDate?: string;
    bn2RDate?: string;
    addr1?: string;
    addr2?: string;
    cstz?: string;
    note?: string;
}

interface IProps {
    isOpen: boolean;
    onClose: () => void;
}

type ViewMode = 'list' | 'form';

const PAGE_SIZE = 25;

const formatDateForDisplay = (value?: string): string => {
    if (!value) {
        return '';
    }

    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) {
        return '';
    }

    return parsed.toLocaleDateString('en-US');
};

const formatDateForInput = (value?: string): string => {
    if (!value) {
        return '';
    }

    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) {
        return '';
    }

    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const normalizeFlag = (value?: string): string => {
    return String(value || '').toUpperCase() === 'Y' ? 'Y' : 'N';
};

const normalizeRecord = (row: Partial<IBNoticeRow>): IBNoticeRow => {
    const mm1099No = Number(row.mm1099No || 0);
    const year = Number(row.year || 0);
    const noticeSeqNo = Number(row.noticeSeqNo || 0);

    return {
        id: `${mm1099No}|${year}|${noticeSeqNo}`,
        mm1099No,
        year,
        noticeSeqNo,
        noticeName: row.noticeName || '',
        tinFromDb: row.tinFromDb || '',
        irsNtype: row.irsNtype || '',
        irsNdate: formatDateForInput(row.irsNdate),
        tsdiDb: normalizeFlag(row.tsdiDb),
        buWh: normalizeFlag(row.buWh),
        noTinName: normalizeFlag(row.noTinName),
        dupIrsNotice: normalizeFlag(row.dupIrsNotice),
        g1IrsNotice3Yr2Bs: normalizeFlag(row.g1IrsNotice3Yr2Bs),
        bn1SDate: formatDateForInput(row.bn1SDate),
        bn2SDate: formatDateForInput(row.bn2SDate),
        bn1RDate: formatDateForInput(row.bn1RDate),
        bn2RDate: formatDateForInput(row.bn2RDate),
        addr1: row.addr1 || '',
        addr2: row.addr2 || '',
        cstz: row.cstz || '',
        note: row.note || '',
    };
};

const toPayload = (row: IBNoticeRow): object => {
    return {
        mm1099No: row.mm1099No,
        year: row.year,
        noticeSeqNo: row.noticeSeqNo,
        noticeName: row.noticeName || '',
        tinFromDb: row.tinFromDb || '',
        irsNtype: row.irsNtype || '',
        irsNdate: row.irsNdate || null,
        tsdiDb: normalizeFlag(row.tsdiDb),
        buWh: normalizeFlag(row.buWh),
        noTinName: normalizeFlag(row.noTinName),
        dupIrsNotice: normalizeFlag(row.dupIrsNotice),
        g1IrsNotice3Yr2Bs: normalizeFlag(row.g1IrsNotice3Yr2Bs),
        bn1SDate: row.bn1SDate || null,
        bn2SDate: row.bn2SDate || null,
        bn1RDate: row.bn1RDate || null,
        bn2RDate: row.bn2RDate || null,
        addr1: row.addr1 || '',
        addr2: row.addr2 || '',
        cstz: row.cstz || '',
        note: row.note || '',
    };
};

const BNoticeMaintenanceDialog: React.FC<IProps> = ({ isOpen, onClose }) => {
    const { get, post, put, response } = useFetch();

    const [viewMode, setViewMode] = useState<ViewMode>('list');
    const [isCreating, setIsCreating] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [mm1099NoFilter, setMm1099NoFilter] = useState('');
    const [yearFilter, setYearFilter] = useState('');
    const [nameFilter, setNameFilter] = useState('');
    const [tinFilter, setTinFilter] = useState('');

    const [pageNumber, setPageNumber] = useState(1);
    const [shouldReload, setShouldReload] = useState(true);
    const [rows, setRows] = useState<PagedResultSet<IBNoticeRow> | undefined>();
    const [editing, setEditing] = useState<IBNoticeRow | null>(null);
    const [requestError, setRequestError] = useState<ILeaseRecordsApiError | null>(null);

    const columns: ITableColumn[] = [
        { name: '1099 No', mapping: 'mm1099No' },
        { name: 'Year', mapping: 'year' },
        { name: 'Notice Seq', mapping: 'noticeSeqNo' },
        { name: 'Notice Name', mapping: 'noticeName' },
        { name: 'TIN', mapping: 'tinFromDb' },
        { name: 'IRS Type', mapping: 'irsNtype' },
        { name: 'IRS Date', mapping: 'irsNdate', format: formatDateForDisplay },
        { name: '1st Sent', mapping: 'bn1SDate', format: formatDateForDisplay },
        { name: '2nd Sent', mapping: 'bn2SDate', format: formatDateForDisplay },
    ];

    const tableConfig: ITableConfig = {
        rowCount: PAGE_SIZE,
        pageNumber,
        idField: 'id',
        columns,
    };

    const loadRows = useCallback(async (): Promise<void> => {
        setIsLoading(true);
        setRequestError(null);

        try {
            const skip = (pageNumber - 1) * PAGE_SIZE;
            const params = new URLSearchParams();
            params.append('skip', skip.toString());
            params.append('top', PAGE_SIZE.toString());

            if (mm1099NoFilter.trim()) {
                params.append('mm1099No', mm1099NoFilter.trim());
            }
            if (yearFilter.trim()) {
                params.append('year', yearFilter.trim());
            }
            if (nameFilter.trim()) {
                params.append('noticeName', nameFilter.trim());
            }
            if (tinFilter.trim()) {
                params.append('tin', tinFilter.trim());
            }

            const data = await get(`/v1.0/leases/admin/1099/b-notices?${params.toString()}`);
            if (!response.ok || !data) {
                setRows(undefined);
                setRequestError(parseLeaseRecordsApiError(response.data || { title: 'Failed to load B-Notice records.' }, response.status));
                return;
            }

            const mapped: IBNoticeRow[] = (data.items || []).map((row: Partial<IBNoticeRow>) => normalizeRecord(row));
            const paged = new PagedResultSet<IBNoticeRow>();
            paged.items = mapped;
            paged.totalRecords = data.totalCount || 0;
            paged.pageNumber = pageNumber;
            paged.pageSize = PAGE_SIZE;
            setRows(paged);
        } catch (error) {
            setRequestError(parseLeaseRecordsApiError({ title: 'Error loading B-Notice records.' }, 500));
            setRows(undefined);
        } finally {
            setIsLoading(false);
        }
    }, [get, mm1099NoFilter, nameFilter, pageNumber, response, tinFilter, yearFilter]);

    useEffect(() => {
        if (isOpen) {
            setViewMode('list');
            setIsCreating(false);
            setEditing(null);
            setShouldReload(true);
            setPageNumber(1);
            setRequestError(null);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || viewMode !== 'list' || !shouldReload) {
            return;
        }

        void loadRows();
        setShouldReload(false);
    }, [isOpen, loadRows, shouldReload, viewMode]);

    const openCreate = (): void => {
        setEditing(
            normalizeRecord({
                mm1099No: 0,
                year: new Date().getFullYear(),
                noticeSeqNo: 1,
                tsdiDb: 'N',
                buWh: 'N',
                noTinName: 'N',
                dupIrsNotice: 'N',
                g1IrsNotice3Yr2Bs: 'N',
            })
        );
        setIsCreating(true);
        setViewMode('form');
    };

    const handleRowClick = async (id: string | number): Promise<void> => {
        const parts = id.toString().split('|');
        if (parts.length !== 3) {
            return;
        }

        const mm1099No = parseInt(parts[0], 10);
        const year = parseInt(parts[1], 10);
        const noticeSeqNo = parseInt(parts[2], 10);

        setIsLoading(true);
        setRequestError(null);
        try {
            const detail = await get(`/v1.0/leases/admin/1099/b-notices/${mm1099No}/${year}/${noticeSeqNo}`);
            if (!response.ok || !detail) {
                setRequestError(parseLeaseRecordsApiError(response.data || { title: 'Failed to load selected B-Notice record.' }, response.status));
                return;
            }

            setEditing(normalizeRecord(detail as Partial<IBNoticeRow>));
            setIsCreating(false);
            setViewMode('form');
        } catch (error) {
            setRequestError(parseLeaseRecordsApiError({ title: 'Error loading selected B-Notice record.' }, 500));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async (): Promise<void> => {
        if (!editing) {
            return;
        }

        if (!editing.mm1099No || !editing.year || !editing.noticeSeqNo) {
            alert('MM 1099 Number, Year, and Notice Seq Number are required.');
            return;
        }

        setIsSaving(true);
        setRequestError(null);
        try {
            const payload = toPayload(editing);
            if (isCreating) {
                await post('/v1.0/leases/admin/1099/b-notices', payload);
            } else {
                await put('/v1.0/leases/admin/1099/b-notices', payload);
            }

            if (!response.ok) {
                setRequestError(parseLeaseRecordsApiError(response.data || { title: 'Failed to save B-Notice record.' }, response.status));
                return;
            }

            setViewMode('list');
            setEditing(null);
            setIsCreating(false);
            setPageNumber(1);
            setShouldReload(true);
        } catch (error) {
            setRequestError(parseLeaseRecordsApiError({ title: 'Error saving B-Notice record.' }, 500));
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.content} onClick={(e) => e.stopPropagation()}>
                <div style={styles.header}>
                    <h3 style={styles.title}>B-Notice Maintenance</h3>
                    <button onClick={onClose} style={styles.closeButton} aria-label="Close B-Notice dialog">
                        <X style={{ width: '1rem', height: '1rem' }} />
                    </button>
                </div>

                <LeaseRecordsErrorAlert error={requestError} onDismiss={() => setRequestError(null)} />

                <div style={styles.body}>
                    {viewMode === 'list' ? (
                        <>
                            <div style={styles.filterGrid}>
                                <Field
                                    label="MM 1099 No."
                                    value={mm1099NoFilter}
                                    editable={true}
                                    onChange={(value) => setMm1099NoFilter(value || '')}
                                />
                                <Field label="Year" value={yearFilter} editable={true} onChange={(value) => setYearFilter(value || '')} />
                                <Field label="Notice Name" value={nameFilter} editable={true} onChange={(value) => setNameFilter(value || '')} />
                                <Field label="TIN" value={tinFilter} editable={true} onChange={(value) => setTinFilter(value || '')} />
                            </div>

                            <div style={styles.actionRow}>
                                <button
                                    onClick={() => {
                                        setPageNumber(1);
                                        setShouldReload(true);
                                    }}
                                    style={styles.secondaryButton}
                                    disabled={isLoading}
                                >
                                    Query
                                </button>
                                <button onClick={openCreate} style={styles.primaryButton}>
                                    Add B-Notice
                                </button>
                            </div>

                            <div className="search-leases__table-wrapper" style={styles.tableSection}>
                                <Table
                                    isLoading={isLoading}
                                    tableConfig={tableConfig}
                                    data={rows}
                                    enablePaging={true}
                                    onPageChange={(newPage) => {
                                        setPageNumber(newPage);
                                        setShouldReload(true);
                                    }}
                                    onRowClick={handleRowClick}
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={styles.formGrid}>
                                <Field
                                    label="MM 1099 Number"
                                    value={String(editing?.mm1099No || '')}
                                    editable={isCreating}
                                    onChange={(value) => setEditing((prev) => (prev ? { ...prev, mm1099No: parseInt(value || '0', 10) || 0 } : prev))}
                                />
                                <Field
                                    label="1099 Year"
                                    value={String(editing?.year || '')}
                                    editable={isCreating}
                                    onChange={(value) => setEditing((prev) => (prev ? { ...prev, year: parseInt(value || '0', 10) || 0 } : prev))}
                                />
                                <Field
                                    label="Notice Seq Number"
                                    value={String(editing?.noticeSeqNo || '')}
                                    editable={isCreating}
                                    onChange={(value) =>
                                        setEditing((prev) => (prev ? { ...prev, noticeSeqNo: parseInt(value || '0', 10) || 0 } : prev))
                                    }
                                />
                                <Field
                                    label="TIN"
                                    value={editing?.tinFromDb || ''}
                                    editable={true}
                                    onChange={(value) => setEditing((prev) => (prev ? { ...prev, tinFromDb: value || '' } : prev))}
                                />
                                <Field
                                    label="Name on Notice"
                                    value={editing?.noticeName || ''}
                                    editable={true}
                                    onChange={(value) => setEditing((prev) => (prev ? { ...prev, noticeName: value || '' } : prev))}
                                />
                                <Field
                                    label="Address 1"
                                    value={editing?.addr1 || ''}
                                    editable={true}
                                    onChange={(value) => setEditing((prev) => (prev ? { ...prev, addr1: value || '' } : prev))}
                                />
                                <Field
                                    label="Address 2"
                                    value={editing?.addr2 || ''}
                                    editable={true}
                                    onChange={(value) => setEditing((prev) => (prev ? { ...prev, addr2: value || '' } : prev))}
                                />
                                <Field
                                    label="City, ST Zip"
                                    value={editing?.cstz || ''}
                                    editable={true}
                                    onChange={(value) => setEditing((prev) => (prev ? { ...prev, cstz: value || '' } : prev))}
                                />
                                <Field
                                    label="IRS Type"
                                    value={editing?.irsNtype || ''}
                                    editable={true}
                                    onChange={(value) => setEditing((prev) => (prev ? { ...prev, irsNtype: value || '' } : prev))}
                                />
                                <Field
                                    label="IRS Date"
                                    type="date"
                                    value={editing?.irsNdate || ''}
                                    editable={true}
                                    onChange={(value) => setEditing((prev) => (prev ? { ...prev, irsNdate: value || '' } : prev))}
                                />
                                <Field
                                    label="1st B Notice Sent"
                                    type="date"
                                    value={editing?.bn1SDate || ''}
                                    editable={true}
                                    onChange={(value) => setEditing((prev) => (prev ? { ...prev, bn1SDate: value || '' } : prev))}
                                />
                                <Field
                                    label="2nd B Notice Sent"
                                    type="date"
                                    value={editing?.bn2SDate || ''}
                                    editable={true}
                                    onChange={(value) => setEditing((prev) => (prev ? { ...prev, bn2SDate: value || '' } : prev))}
                                />
                                <Field
                                    label="1st B Notice Received"
                                    type="date"
                                    value={editing?.bn1RDate || ''}
                                    editable={true}
                                    onChange={(value) => setEditing((prev) => (prev ? { ...prev, bn1RDate: value || '' } : prev))}
                                />
                                <Field
                                    label="2nd B Notice Received"
                                    type="date"
                                    value={editing?.bn2RDate || ''}
                                    editable={true}
                                    onChange={(value) => setEditing((prev) => (prev ? { ...prev, bn2RDate: value || '' } : prev))}
                                />
                                <div style={styles.checkboxRow}>
                                    <CheckboxField
                                        label="TSDI DB"
                                        checked={normalizeFlag(editing?.tsdiDb) === 'Y'}
                                        editable={true}
                                        onChange={(checked) => setEditing((prev) => (prev ? { ...prev, tsdiDb: checked ? 'Y' : 'N' } : prev))}
                                    />
                                    <CheckboxField
                                        label="B/U W/H"
                                        checked={normalizeFlag(editing?.buWh) === 'Y'}
                                        editable={true}
                                        onChange={(checked) => setEditing((prev) => (prev ? { ...prev, buWh: checked ? 'Y' : 'N' } : prev))}
                                    />
                                    <CheckboxField
                                        label="No TIN or Name"
                                        checked={normalizeFlag(editing?.noTinName) === 'Y'}
                                        editable={true}
                                        onChange={(checked) => setEditing((prev) => (prev ? { ...prev, noTinName: checked ? 'Y' : 'N' } : prev))}
                                    />
                                    <CheckboxField
                                        label="Duplicate IRS Notice"
                                        checked={normalizeFlag(editing?.dupIrsNotice) === 'Y'}
                                        editable={true}
                                        onChange={(checked) => setEditing((prev) => (prev ? { ...prev, dupIrsNotice: checked ? 'Y' : 'N' } : prev))}
                                    />
                                    <CheckboxField
                                        label="> 1 IRS Notice in 3 yrs 2nd B Status"
                                        checked={normalizeFlag(editing?.g1IrsNotice3Yr2Bs) === 'Y'}
                                        editable={true}
                                        onChange={(checked) =>
                                            setEditing((prev) => (prev ? { ...prev, g1IrsNotice3Yr2Bs: checked ? 'Y' : 'N' } : prev))
                                        }
                                    />
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <Field
                                        label="Note"
                                        type="textarea"
                                        value={editing?.note || ''}
                                        editable={true}
                                        onChange={(value) => setEditing((prev) => (prev ? { ...prev, note: value || '' } : prev))}
                                    />
                                </div>
                            </div>

                            <div style={styles.actionRow}>
                                <button onClick={handleSave} style={styles.primaryButton} disabled={isSaving}>
                                    <div style={styles.buttonInner}>
                                        {isSaving && <Loader2 className="animate-spin" style={{ width: '1rem', height: '1rem' }} />}
                                        <span>{isSaving ? 'Saving...' : 'Save Record'}</span>
                                    </div>
                                </button>
                                <button
                                    onClick={() => {
                                        setViewMode('list');
                                        setEditing(null);
                                        setIsCreating(false);
                                    }}
                                    style={styles.secondaryButton}
                                    disabled={isSaving}
                                >
                                    Close Screen
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 95,
        padding: '1rem',
    },
    content: {
        width: 'min(1280px, 96vw)',
        maxHeight: '92vh',
        backgroundColor: '#FFFFFF',
        borderRadius: '0.75rem',
        boxShadow: '0 24px 48px rgba(17, 24, 39, 0.25)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.9rem 1rem',
        borderBottom: '1px solid #E5E7EB',
    },
    title: {
        margin: 0,
        fontSize: '1.1rem',
        fontWeight: 700,
        color: '#1F2937',
    },
    closeButton: {
        border: 'none',
        background: 'transparent',
        color: '#6B7280',
        cursor: 'pointer',
        padding: '0.2rem',
    },
    body: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.9rem',
        padding: '0.9rem',
        overflow: 'hidden',
        minHeight: 0,
        flex: 1,
    },
    filterGrid: {
        display: 'grid',
        gap: '0.7rem',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    },
    formGrid: {
        display: 'grid',
        gap: '0.7rem',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        overflowY: 'auto',
        paddingRight: '0.25rem',
    },
    tableSection: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
    },
    actionRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '0.6rem',
    },
    buttonInner: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
    },
    primaryButton: {
        border: 'none',
        borderRadius: '0.4rem',
        padding: '0.45rem 0.9rem',
        backgroundColor: '#1D4ED8',
        color: '#FFFFFF',
        cursor: 'pointer',
        fontWeight: 600,
    },
    secondaryButton: {
        border: '1px solid #CBD5E1',
        borderRadius: '0.4rem',
        padding: '0.45rem 0.9rem',
        backgroundColor: '#FFFFFF',
        color: '#0F172A',
        cursor: 'pointer',
        fontWeight: 600,
    },
    checkboxRow: {
        gridColumn: '1 / -1',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: '0.5rem 1rem',
        paddingTop: '0.2rem',
    },
};

export default BNoticeMaintenanceDialog;



// WEBPACK FOOTER //
// ./src/components/leases/sections/BNoticeMaintenanceDialog.tsx